import {
  DiveVersion,
  MODEL_CONTENT_TYPE,
  MODEL_MAX_BYTES,
  TERRAIN_CONTENT_TYPE,
  decodeTerrain,
  routes,
} from "@divespots/contracts";
import type {
  AdminUserCreate,
  AdminUserListQuery,
  SiteSettingsUpdate,
  AdminUserUpdate,
  DiveObjectCreate,
  MeUpdate,
  SightingCreate,
  SuggestionApprove,
  SuggestionCreate,
  SuggestionReject,
  DiveObjectUpdate,
  LakeUpdate,
  ObjectModelListQuery,
  ObjectModelUpdate,
  ObjectListQuery,
  SpotCreate,
  SpotUpdate,
  Terrain,
} from "@divespots/contracts";
import type { RouteName, SpotListQueryInput } from "@divespots/contracts";
import type { z } from "zod";
import { getApiMode } from "./config.ts";
import { retryWhenBusy } from "./retry.ts";
import { ApiRequestError, errorFromResponse } from "./errors.ts";
import { fixturesTransport } from "./fixtures-transport.ts";
import { liveTransport } from "./live-transport.ts";
import type { Transport } from "./transport.ts";

type RouteResponse<K extends RouteName> = (typeof routes)[K] extends {
  response: infer S extends z.ZodType;
}
  ? z.infer<S>
  : never;

interface CallOptions {
  params?: Record<string, string>;
  query?: Record<string, string | undefined>;
  body?: unknown;
  rawBody?: ArrayBuffer;
  rawContentType?: string;
  signal?: AbortSignal | undefined;
}

function transport(): Transport {
  return getApiMode() === "live" ? liveTransport : fixturesTransport;
}

/**
 * The one place a response enters the app: every body is parsed with its contracts schema
 * (or `ApiError` for non-2xx) before anything else sees it.
 */
async function call<K extends RouteName>(
  name: K,
  opts: CallOptions = {},
): Promise<RouteResponse<K>> {
  const def = routes[name];
  const res = await transport()({
    route: name,
    params: opts.params ?? {},
    query: opts.query ?? {},
    body: opts.body,
    ...(opts.rawBody === undefined
      ? {}
      : { rawBody: opts.rawBody, rawContentType: opts.rawContentType ?? MODEL_CONTENT_TYPE }),
    signal: opts.signal,
  });
  if (res.status !== def.status) throw errorFromResponse(res.status, res.json);
  if (def.status === 204) return undefined as RouteResponse<K>;
  const schema = "response" in def ? def.response : undefined;
  if (!schema || schema === "binary") {
    throw new ApiRequestError("not_implemented", `${name} has no JSON response`, res.status);
  }
  const parsed = (schema as z.ZodType).safeParse(res.json);
  if (!parsed.success) {
    throw new ApiRequestError(
      "invalid_response",
      `${name}: response does not match the contract`,
      res.status,
      parsed.error,
    );
  }
  return parsed.data as RouteResponse<K>;
}

export interface TerrainParams {
  lat: number;
  lon: number;
  radius: number;
}

/** Fixed-point so tiny or large numbers never become exponent notation (the contract's NumParam is strict). */
const num = (n: number) => n.toFixed(7).replace(/\.?0+$/, "");

/** The query of a terrain request; the offline saver builds the same URL, so cache keys match. */
export const terrainQuery = ({ lat, lon, radius }: TerrainParams) => ({
  lat: num(lat),
  lon: num(lon),
  radius: String(Math.round(radius)),
});

/** Binary routes: checks status and Content-Type, then decodes with the contracts codec only. */
async function getBinaryTerrain(
  route: "terrain" | "diveTile" | "diveOverview" | "lakeOverview",
  opts: Pick<CallOptions, "params" | "query" | "signal">,
): Promise<Terrain> {
  const res = await transport()({
    route,
    params: opts.params ?? {},
    query: opts.query ?? {},
    signal: opts.signal,
  });
  if (res.status !== routes[route].status) throw errorFromResponse(res.status, res.json);
  if (!res.contentType?.toLowerCase().startsWith(TERRAIN_CONTENT_TYPE) || !res.binary) {
    throw new ApiRequestError(
      "invalid_response",
      `${route}: unexpected content type ${res.contentType ?? "none"}`,
      res.status,
    );
  }
  try {
    return decodeTerrain(res.binary);
  } catch (e) {
    throw new ApiRequestError(
      "invalid_response",
      e instanceof Error ? e.message : `${route}: undecodable body`,
      res.status,
      e,
    );
  }
}

async function getTerrain(
  { lat, lon, radius }: TerrainParams,
  signal?: AbortSignal,
): Promise<Terrain> {
  // E2E builds only: the tests can see which tiles were asked for (a retraced tile must not be)
  if (import.meta.env.VITE_E2E === "1") {
    const w = window as unknown as { __terrainAsked?: string[] };
    (w.__terrainAsked ??= []).push(`${lat.toFixed(5)},${lon.toFixed(5)}`);
  }
  return getBinaryTerrain("terrain", { query: terrainQuery({ lat, lon, radius }), signal });
}

/** The bytes of a model file (`/api/object-models/<sha256>.glb`), after a content-type check. */
async function getModelFile(file: string, signal?: AbortSignal): Promise<ArrayBuffer> {
  const res = await retryWhenBusy(
    () => transport()({ route: "getObjectModelFile", params: { file }, query: {}, signal }),
    signal,
  );
  if (res.status !== routes.getObjectModelFile.status)
    throw errorFromResponse(res.status, res.json);
  if (!res.contentType?.toLowerCase().startsWith(MODEL_CONTENT_TYPE) || !res.binary) {
    throw new ApiRequestError(
      "invalid_response",
      `model file: unexpected content type ${res.contentType ?? "none"}`,
      res.status,
    );
  }
  return res.binary;
}

/** Upload metadata: numbers become strings on the wire. */
export interface ModelUploadParams {
  attribution: string;
  scale: number;
  headingDeg: number;
}

/** Query of the suggestion lists; numbers become strings on the wire. */
export interface SuggestionListParams {
  status?: "pending" | "approved" | "rejected" | "withdrawn";
  kind?: "spot_create" | "object_create" | "spot_edit" | "object_edit";
  submittedBy?: string;
  limit?: number;
  before?: string;
}
const suggestionQuery = (q: SuggestionListParams) => ({
  ...q,
  limit: q.limit === undefined ? undefined : String(q.limit),
});

export type SpotListParams = SpotListQueryInput;
export type ObjectListParams = ObjectListQuery;

export const api = {
  health: (signal?: AbortSignal) => call("health", { signal }),
  listLakes: (signal?: AbortSignal) => call("listLakes", { signal }),
  /** Public whole-lake overview terrain for the mini-maps; `meta.waterLevelM` is the live level. */
  lakeOverview: (slug: string, signal?: AbortSignal) =>
    getBinaryTerrain("lakeOverview", { params: { slug }, signal }),
  /** Public wind forecast grid for the map overlay (a model forecast, see contracts/src/wind.ts). */
  windField: (signal?: AbortSignal) => call("wind", { signal }),
  /** Public precipitation forecast grid for the map overlay (model forecast, see contracts/src/precip.ts). */
  precipField: (signal?: AbortSignal) => call("precip", { signal }),
  listSpots: (query: SpotListParams = {}, signal?: AbortSignal) =>
    call("listSpots", { query: { ...query }, signal }),
  getSpot: (slug: string, signal?: AbortSignal) => call("getSpot", { params: { slug }, signal }),
  getDepth: (lat: number, lon: number, signal?: AbortSignal) =>
    call("depth", { query: { lat: num(lat), lon: num(lon) }, signal }),
  getTerrain,

  // Phase 2/3 stubs (3D terrain, admin), named after the contract route table.
  // Moderator/admin routes (cookie session, same-origin).
  me: (signal?: AbortSignal) => call("me", { signal }),
  meFeatures: (signal?: AbortSignal) => call("meFeatures", { signal }),

  // Dive mode (beta): auth "dive", see contracts/src/dive.ts.
  listDiveLakes: (signal?: AbortSignal) => call("listDiveLakes", { signal }),
  diveManifest: (slug: string, signal?: AbortSignal) =>
    call("diveManifest", { params: { slug }, signal }),
  diveTile: (slug: string, x: number, y: number, v: string, signal?: AbortSignal) =>
    getBinaryTerrain("diveTile", {
      params: { slug, x: String(x), y: String(y) },
      // an empty `v` would be dropped from the URL and silently switch stale detection off
      query: { v: DiveVersion.parse(v) },
      signal,
    }),
  diveOverview: (slug: string, v: string, signal?: AbortSignal) =>
    getBinaryTerrain("diveOverview", {
      params: { slug },
      query: { v: DiveVersion.parse(v) },
      signal,
    }),
  listObjects: (query: ObjectListParams, signal?: AbortSignal) =>
    call("listObjects", { query: { ...query }, signal }),
  createSpot: (body: SpotCreate) => call("createSpot", { body }),
  updateSpot: (id: string, body: SpotUpdate) => call("updateSpot", { params: { id }, body }),
  deleteSpot: (id: string) => call("deleteSpot", { params: { id } }),
  // 3D models of objects (reads: public; writes: admin only).
  listObjectModels: (query: ObjectModelListQuery, signal?: AbortSignal) =>
    call("listObjectModels", { query: { ...query }, signal }),
  getObjectModelFile: getModelFile,
  putObjectModel: (id: string, file: ArrayBuffer, p: ModelUploadParams) => {
    // the server is authoritative, but a body over the limit never needs to leave the browser
    const size = file.byteLength;
    if (size > MODEL_MAX_BYTES) {
      return Promise.reject(new ApiRequestError("payload_too_large", "model file too large", 413));
    }
    return call("putObjectModel", {
      params: { id },
      query: {
        attribution: p.attribution,
        scale: String(p.scale),
        headingDeg: String(p.headingDeg),
      },
      rawBody: file,
      rawContentType: MODEL_CONTENT_TYPE,
    });
  },
  updateObjectModel: (id: string, body: ObjectModelUpdate) =>
    call("updateObjectModel", { params: { id }, body }),
  deleteObjectModel: (id: string) => call("deleteObjectModel", { params: { id } }),
  createObject: (body: DiveObjectCreate) => call("createObject", { body }),
  updateObject: (id: string, body: DiveObjectUpdate) =>
    call("updateObject", { params: { id }, body }),
  deleteObject: (id: string) => call("deleteObject", { params: { id } }),
  updateLake: (slug: string, body: LakeUpdate) => call("updateLake", { params: { slug }, body }),
  listUsers: (query: AdminUserListQuery = {}, signal?: AbortSignal) =>
    call("listUsers", { query: { ...query }, signal }),
  /** Admin action log; `query` is built by `auditQuery` (filters, limit, `before` cursor). */
  listAudit: (query: Record<string, string> = {}, signal?: AbortSignal) =>
    call("listAudit", { query, signal }),
  /** Public: lets the login page say "registration closed" up front. */
  publicConfig: (signal?: AbortSignal) => call("publicConfig", { signal }),
  getSettings: (signal?: AbortSignal) => call("getSettings", { signal }),
  updateSettings: (body: SiteSettingsUpdate) => call("updateSettings", { body }),
  createUser: (body: AdminUserCreate) => call("createUser", { body }),
  updateUser: (id: string, body: AdminUserUpdate) => call("updateUser", { params: { id }, body }),

  // Diver accounts, suggestions, sightings (wave 2).
  updateMe: (body: MeUpdate) => call("updateMe", { body }),
  deleteMe: () => call("deleteMe"),
  listSightings: (id: string, signal?: AbortSignal) =>
    call("listSightings", { params: { id }, signal }),
  createSighting: (id: string, body: SightingCreate) =>
    call("createSighting", { params: { id }, body }),
  createSuggestion: (body: SuggestionCreate) => call("createSuggestion", { body }),
  listMySuggestions: (
    q: Pick<SuggestionListParams, "limit" | "before"> = {},
    signal?: AbortSignal,
  ) => call("listMySuggestions", { query: suggestionQuery(q), signal }),
  listSuggestions: (q: SuggestionListParams = {}, signal?: AbortSignal) =>
    call("listSuggestions", { query: suggestionQuery(q), signal }),
  getSuggestion: (id: string, signal?: AbortSignal) =>
    call("getSuggestion", { params: { id }, signal }),
  approveSuggestion: (id: string, body: SuggestionApprove) =>
    call("approveSuggestion", { params: { id }, body }),
  rejectSuggestion: (id: string, body: SuggestionReject) =>
    call("rejectSuggestion", { params: { id }, body }),
  withdrawSuggestion: (id: string) => call("withdrawSuggestion", { params: { id } }),
} as const;

export const { listLakes, listSpots, getSpot, getDepth, health } = api;
